import './create-issuer.scss'

import { notification } from 'antd'
import axios from 'axios'
import { Formik, FormikHelpers } from 'formik'
import { Form, FormItem, Input, Select, SubmitButton, Switch } from 'formik-antd'
import React from 'react'
import { Column } from 'react-display-flex'
import useSWR from 'swr'

import { fetcher } from '../../../../api'
import { filterProps, parseApiErrorResponse } from '../../../../components/forms/utils'
import { SelectRating } from '../../../../components/SelectRating'
import { messages } from '../../messages'
import { Issuer, issuerFormInitialValues, IssuerFormValues } from '../../models'
import { issuerValidationSchema } from './validation'

interface Props {
  onSuccess: () => void
}

export const CreateIssuer = ({ onSuccess }: Props) => {
  const { data: bankingGroups } = useSWR<Issuer[]>('/banking-groups', fetcher)

  const onSubmit = async (
    issuerFormValues: IssuerFormValues,
    { setSubmitting, resetForm, setErrors }: FormikHelpers<IssuerFormValues>,
  ) => {
    const issuerPayloadValues = convertFormValuesToIssuerRequestPayload(issuerFormValues)

    try {
      await axios.post('/issuers/', issuerPayloadValues)

      resetForm()
      notification.success({ message: messages.common.success, description: messages.update.success })
      onSuccess()
    } catch (error) {
      const formErrors = parseApiErrorResponse(error)
      setErrors(formErrors)
      setSubmitting(false)
      notification.error({ message: messages.common.error, description: messages.update.error })
    }
  }

  return (
    <Formik initialValues={issuerFormInitialValues} validationSchema={issuerValidationSchema} onSubmit={onSubmit}>
      {({ values }) => (
        <Form name="create-issuer">
          <FormItem name="shortName" label="Display Name" required>
            <Input name="shortName" placeholder="Issuer name" />
          </FormItem>
          <FormItem name="name" label="Long Name" required>
            <Input name="name" placeholder="Issuer descriptive name" />
          </FormItem>
          <FormItem name="code" label="Code" required>
            <Input name="code" placeholder="Code" />
          </FormItem>
          <FormItem name="belongsToBankingGroup" label="Belongs to Banking Group?">
            <Switch checkedChildren="Yes" unCheckedChildren="No" name="belongsToBankingGroup" />
          </FormItem>
          <FormItem name="bankingGroupId" label="Banking Group" required={values.belongsToBankingGroup}>
            <Select
              name="bankingGroupId"
              placeholder="Select the banking group"
              showSearch
              loading={!bankingGroups}
              options={bankingGroups?.map(({ id, name, code }) => ({ label: `${name} (${code})`, value: id }))}
              {...filterProps}
              disabled={!values.belongsToBankingGroup}
            />
          </FormItem>
          <FormItem name="longRating" label="Long Short Rating" required={!values.belongsToBankingGroup}>
            <SelectRating name="longRating" longRatings disabled={values.belongsToBankingGroup} />
          </FormItem>
          <FormItem name="shortRating" label="Short Rating" required={!values.belongsToBankingGroup}>
            <SelectRating name="shortRating" disabled={values.belongsToBankingGroup} />
          </FormItem>
          <FormItem name="backOfficeEmail" label="Back Office Email" required>
            <Select name="backOfficeEmail" placeholder="Back Office Email" mode="tags" allowClear />
          </FormItem>
          <FormItem name="frontOfficeEmail" label="Front Office Email" required>
            <Select name="frontOfficeEmail" placeholder="Front Office Email" mode="tags" allowClear />
          </FormItem>
          <FormItem name="leiCode" label="LEI Code">
            <Input name="leiCode" placeholder="Lei Code" />
          </FormItem>
          <FormItem name="clearCode" label="Clear Code">
            <Input name="clearCode" placeholder="Clear Code" />
          </FormItem>
          <FormItem name="fundsFossilFuels" label="Funds Fossil Fuels">
            <Switch name="fundsFossilFuels" checkedChildren="Yes" unCheckedChildren="No" />
          </FormItem>
          <Column>
            <SubmitButton htmlType="submit" type="primary">
              Create Issuer
            </SubmitButton>
          </Column>
        </Form>
      )}
    </Formik>
  )
}

const convertFormValuesToIssuerRequestPayload = (values: IssuerFormValues) => ({
  ...values,
  backOfficeEmail: values.backOfficeEmail.join(','),
  frontOfficeEmail: values.frontOfficeEmail.join(','),
  bankingGroupId: values.belongsToBankingGroup ? values.bankingGroupId : undefined,
  longRating: values.belongsToBankingGroup ? undefined : values.longRating,
  shortRating: values.belongsToBankingGroup ? undefined : values.shortRating,
})
